<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import * as ort from "onnxruntime-web";
import * as processing from "@/assets/modelrunner/processing";
import { query } from "vue-gtag";
import { errorMessages } from "@vue/compiler-sfc";

const props = defineProps({
  modelURL: { type: String, default: "" },
});
const route = useRoute();

// --------------------- UI State ---------------------
const loadingState = ref("");
const loadedFileName = ref("");
const loadError =ref("");
const count=ref(0);
const results=ref([]);

// Default mode is "upload"
const mode = ref("single_image");
const videoEl = ref(null);
const imgEl = ref(null);
const canvasEl = ref(null);
const fileInput = ref(null);
const isDragging = ref(false);
const isProcessingImage = ref(false);
const bottomMessage = ref("");
const cameraList = ref("");
const selectedCamera = ref("");
const hideBoxes =ref(false);

var cameraStream = null;
var isRunningLiveInference = false; // prevent overlapping inferences
var canvasCtx=null;
var labels=[];
var runnerOK=false;
var model_params={};
var classColors=[];
var model_output={};
var loadAttempts=0;                 // retry counter for transient load failures
const MAX_LOAD_ATTEMPTS=3;
const LOAD_RETRY_DELAY_MS=1500;
//---------------------- worker Thread ------------------
const worker = new Worker(new URL('@/assets/modelrunner/worker.js', import.meta.url));
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

worker.onmessage = (e) => {
  if (e.data.type === 'loaded') {
    console.log("Model loaded");
    loadAttempts = 0;
    onModelLoaded(e.data.params);
  }

  if (e.data.type === 'load_error') {
     // Transient failures (e.g. a dropped/timed-out model download) are common
     // against remote backends; retry the whole load a few times before giving up.
     if (loadAttempts < MAX_LOAD_ATTEMPTS) {
       loadAttempts += 1;
       bottomMessage.value = `Model load failed, retrying (${loadAttempts}/${MAX_LOAD_ATTEMPTS})…`;
       setTimeout(() => loadModel(), LOAD_RETRY_DELAY_MS * loadAttempts);
       return;
     }
     bottomMessage.value = (e.data.params && e.data.params.error) || 'Failed to load model';
     onModelLoaded(e.data.params);
  }
  if (e.data.type === 'run_error') {
    isProcessingImage.value=false;
    isRunningLiveInference = false;  
    bottomMessage.value=e.data.err;
    
  }
  if (e.data.type === 'result') {
    isProcessingImage.value=false;

    //Catch the last live results when changing from live to image
    if(isRunningLiveInference && mode.value=='single_image') {
      let w=canvasEl.value.width;
      let h=canvasEl.value.height;
      canvasCtx.clearRect(0, 0, w,h);
    } else {
      model_output=e.data.output
      OnModelResults();
    }
    isRunningLiveInference = false; 
    
  }
};

const OnModelResults = async () => {
    try {
      const output=model_output;

      let w=canvasEl.value.width;
      let h=canvasEl.value.height;

      let detectObjects=[];
      results.value=[];

      canvasCtx.clearRect(0, 0, w,h);

      if (output.mask) {

        let maskObject= {
          mask: output.mask,
          color: [255,255,0,170],
        }

        let masks=[maskObject];
        drawSegmentationMask(canvasCtx,masks);
      }
      

      if(output.boxes) {
        if (output.boxes.length){
          //Draw objects Masks of they are specific to objects
          for (let k=0;k<output.boxes.length;k++) {
             drawObjectMask(canvasCtx,output.boxes[k]);
          }
         

          for (let k=0;k<output.boxes.length;k++) {
            const o=output.boxes[k];
            const box=o.box;
            let c=o.class;
            let classname=labels[c];
          
            let obj= {
              rect:[box[0]*w,box[1]*h,box[2]*w,box[3]*h],
              label:classname,
              score:o.score,
              mask:o.mask,
            };
            detectObjects.push(obj);    
          }      
          drawBoxes(canvasCtx,detectObjects);
        }
      }

    

    } catch (err) {
      console.error("Inference error:", err);
    }

};

// --------------------- Load Model ---------------------
const loadModel = async () => {
  if (!props.modelURL) return;

  loadingState.value="loading"
  loadedFileName.value = "";
  bottomMessage.value="Loading Model ..."
 
  worker.postMessage({
    type: "load",
    params: {
      url:props.modelURL,
      labels:labels,
      type:model_params.type,
      iou_threshold:0.7,
      score_threshold:0.25,
      nc:labels.length,
    }
  });
}

const onModelLoaded = async (params) => {
  model_params=params;
  if (params && params.ok){
    // Sync local labels with what the worker resolved (may include ONNX metadata fallback)
    if (params.labels && params.labels.length > 0) {
      labels = params.labels;
    }
    loadedFileName.value = route.query.model;
    loadingState.value="ok";
    runnerOK=true;
    generateColors();
  } else {
    console.error("Model load error:", params && params.error);
    runnerOK=false;
    loadError.value = (params && params.error) ? params.error : "Error loading model";
    loadingState.value="error";
  }
  bottomMessage.value=""
};

watch(
  () => props.modelURL,
  async (newVal) => {
      loadAttempts = 0;
      await getModelDetails();
      await loadModel();
  }
);
// --------------------- Camera ---------------------
const startCamera = async () => {
  if(!selectedCamera.value) return;
  console.log("Camera:",selectedCamera.value);

  try{
    bottomMessage.value="Starting Live View - Please wait ..."
    stopCamera();

    cameraStream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: selectedCamera.value } },
      audio: false,
    });

    
    videoEl.value.srcObject = cameraStream;
    await videoEl.value.play();

    let w=videoEl.value.clientWidth;
    let h=videoEl.value.clientHeight;
    canvasEl.value.width=w;
    canvasEl.value.height=h;
    bottomMessage.value=""
  } catch(err) {
     console.log("Cannot Start Live stream",err);
  }
  runLiveLoop();

};

const stopCamera = () => {
  if (!cameraStream) return;
  cameraStream.getTracks().forEach((t) => t.stop());
  cameraStream = null;
};

async function listCameras() {
  try {
     
    selectedCamera.value='';
    // Step 1 — Request temporary camera access
    await navigator.mediaDevices.getUserMedia({ video: true });

    // Step 2 — Now enumerate devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter(d => d.kind === "videoinput");

    console.log("Cameras after permission:", cameras);

    // Step 3 — Save first camera
    if (cameras.length > 0) {
      cameraList.value = cameras;
      selectedCamera.value = cameras[0].deviceId;
    }

    return cameras.length;
  } catch (err) {
    console.error("Camera access error:", err);
    return 0;
  }
}


async function toggleCamera() {
  if (cameraList.value.length<2) return;

  let index=0;
  for (let k=0;k<cameraList.value.length;k++){
    if (cameraList.value[k].deviceId==selectedCamera.value) {
      index=k;
      break;
    }
  }
  index++;
  if (index>=cameraList.value.length) index=0;
  selectedCamera.value=cameraList.value[index].deviceId;
  if(mode.value=='live') {
     startCamera();
  }
}

// --------------------- Live Loop ---------------------
const runLiveLoop = async () => {
  if (!runnerOK || !videoEl.value) return;

  const loop = async () => {
    if (mode.value!='live') return;
    if (!cameraStream) return;
    if (mode.value!='live') return;

    if (!isRunningLiveInference) {
      isRunningLiveInference = true;
     
      //Commented below - enable to verify what image is being processed
      // canvasCtx.drawImage(videoEl.value,0,0,canvasEl.value.width,canvasEl.value.heights);
      // canvasCtx.strokeStyle = "red";
      // canvasCtx.fillStyle = "red";
      // canvasCtx.strokeRect(20, 20, 100, 100);

      RunModel(videoEl.value);
            
    }
    requestAnimationFrame(loop);
  };

  loop();
};

// --------------------- Uploaded Image ---------------------
const handleFile = (file) => {
  if (!file) return;

  imgEl.value.src = URL.createObjectURL(file);
  imgEl.value.onload =  () => {
      //Get the canvas width as it is 100%
      //The height is calculates according to image aspect ratio
      let w=canvasEl.value.width;
      let h=(imgEl.value.naturalHeight*w)/imgEl.value.naturalWidth;
      canvasEl.value.height=h;
      imgEl.value.width=canvasEl.value.clientWidth;
      imgEl.value.height=canvasEl.value.clientHeight;
      isProcessingImage.value=true;
      setTimeout(() =>  RunModel(imgEl.value), 50);
  }
};

// --------------------- Run Model on image ---------------------
const RunModel = async (sourceEl) => {
    if (!runnerOK) return;
    try {
      model_params.labels=labels;
      count.value=count.value+1;
      //console.log("Frame",count.value);
      const data = processing.getInputFromSource(sourceEl,model_params.width,model_params.height);
      worker.postMessage({
        type: "run",
        params:model_params,
        buffer: data.buffer,
      }, [data.buffer]);
    } catch (err) {
      console.error("Inference error:", err);
    }
};


const onFileSelected = (e) => handleFile(e.target.files[0]);

// Drag/drop handlers
const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; };
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; };
const onDragOver = (e) => e.preventDefault();
const onDrop = (e) => {
  e.preventDefault();
  isDragging.value = false;
  handleFile(e.dataTransfer.files[0]);
};

// --------------------- Watch Mode ---------------------

watch(mode, async (v) => {
  bottomMessage.value='';

  console.log("Mode",v);
  canvasCtx.clearRect(0,0,canvasEl.value.width,canvasEl.value.height);

  if (v === "live") {
    imgEl.value.src = "";
    await nextTick();
    listCameras();
    startCamera();
  } else {
    stopCamera();
  }
});

watch(selectedCamera, async (v) => {
  if(mode.value=='live') {
     startCamera();
  }
 
});

watch(hideBoxes, (v) => {
  OnModelResults();
});

//---------------------- Utility Functions -------------------
function getContext(canvas) {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  // Increase internal resolution
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;

  const ctx = canvas.getContext("2d",{ willReadFrequently: true });
  ctx.scale(dpr, dpr);

  return ctx;
}

function labelToDarkRGBUint8(label) {
  let hash = 0;

  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) | 0;
  }

  // Keep colors dark: 0–120 range
  const r = (hash >> 16) & 0x7f;
  const g = (hash >> 8) & 0x7f;
  const b = hash & 0x7f;

  return new Uint8Array([r, g, b]);
}

function generateColors() {
  for (const label of model_params.labels){
    classColors[label] = labelToDarkRGBUint8(label);
  }
}

/**
 * Draw bounding boxes with labels outside the box.
 * @param {CanvasRenderingContext2D} ctx 
 * @param {Array} objects - [{ rect:[x1,y1,x2,y2], label, score }]
 */
function drawBoxes(ctx, objects) {
  if (hideBoxes.value) return;

  ctx.save();
  ctx.lineWidth = 2;
  ctx.font = "12px Arial"; // smaller font
  ctx.textBaseline = "bottom";

  for (const obj of objects) {
    const [x1, y1, x2, y2] = obj.rect;
    const label = obj.label;

    const [r, g, b] = classColors[label];
    const color = `rgb(${r}, ${g}, ${b})`;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 4;

    const width = x2 - x1;
    const height = y2 - y1;

    // Draw rectangle
    ctx.strokeRect(x1, y1, width, height);

    // ---- TEXT OUTSIDE THE BOX ----
    const textHeight = 22; // same as font size
    ctx.font = "22px sans-serif";
    // const text = `${label} : ${score}`;    Hide Score until it is fixed
    const text = `${label}`;
    const textWidth = ctx.measureText(text).width;
    
    // draw background above the box
    ctx.fillRect(x1-1, y1 - textHeight-6 , textWidth + 6, textHeight + 6);

    // draw text
    ctx.fillStyle = "white";
    ctx.fillText(text, x1 + 3, y1);
  }

  ctx.restore();
}


/**
 * Draw a single segmentation mask overlay from an ONNX tensor.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} maskTensor - { data, dims:[H,W] }
 * @param {Array} color - [r, g, b, a] (0–255)
 */
function drawSegmentationMask(ctx, maskObjects) {

  let maskTensor=maskObjects[0].mask;

  const canvas = ctx.canvas;
  const [maskHeight, maskWidth] = maskTensor.dims;

  for (const o of maskObjects) {
    let mask=o.mask.cpuData;
 
    const imageData = ctx.createImageData(maskWidth, maskHeight);
    const data = imageData.data;

    for (let i = 0; i < mask.length; i++) {
      let m=mask[i];
      if (m < model_params.nc ) {
        const label=model_params.labels[m];
        const [r, g, b] = classColors[label];

        const idx = i * 4;
        data[idx]     = r;
        data[idx + 1] = g;
        data[idx + 2] = b;
        data[idx + 3] = 190;
      }
    }

    // Scale mask to canvas size
    const tmp = document.createElement("canvas");
    tmp.width = maskWidth;
    tmp.height = maskHeight;
    tmp.getContext("2d").putImageData(imageData, 0, 0);

    ctx.drawImage(tmp, 0, 0, canvas.width, canvas.height);
  }

}

/**
 * Draw a single segmentation mask overlay for a single object.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} maskTensor - { data, dims:[H,W] }
 * @param {Array} color - [r, g, b, a] (0–255)
 */
function drawObjectMask(ctx, obj) {

  if(!obj.mask) return;

  const mask=obj.mask.data;

  const canvas = ctx.canvas;
  const maskHeight = obj.mask.height;
  const maskWidth = obj.mask.width

  const imageData = ctx.createImageData(maskWidth, maskHeight);
  const data = imageData.data;

  for (let y = 0; y < maskHeight; y++) 
  for (let x = 0; x < maskWidth; x++) 
  {
    const i=x+y*maskWidth;
    //ignore pixels outside objects bbx
    if(x<obj.box[0]*maskWidth || y<obj.box[1]*maskHeight || x>obj.box[2]*maskWidth || y>obj.box[3]*maskHeight) continue;

    let m=mask[i];
    if (m > 0.5) {
      const label=model_params.labels?.[obj.class];
      const color = label ? classColors[label] : null;
      if (!color) continue;
      const [r, g, b] = color;

      const idx = i * 4;
      data[idx]     = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = 190;
    }
  }

  // Scale mask to canvas size
  const tmp = document.createElement("canvas");
  tmp.width = maskWidth;
  tmp.height = maskHeight;
  tmp.getContext("2d").putImageData(imageData, 0, 0);

  ctx.drawImage(tmp, 0, 0, canvas.width, canvas.height);

}

async function getModelDetails () {
  const dataConnect = new DatastoreConnect();
  let trainer_id = parseInt(route.query.trainer);
  const result = await dataConnect.getModelInfo({ trainer_session_id: trainer_id })
    .then(async (data) => {
      if (!data.result) {
        bottomMessage.value="Failed to get model details";
      } else {
        const model = data.result;
        if (model.labels) {
          labels=model.labels;
        }
        if (model.type) {
          model_params.type=model.type;
        }
      }
    })
    .catch((error) => {
       bottomMessage.value="Failed to get model details";
    });
  
}

// --------------------- Lifecycle ---------------------
onMounted(async () => {
  canvasCtx=getContext(canvasEl.value);
  if (props.modelURL) {
      await getModelDetails();
      await loadModel();
    }
 
  if (mode.value === "live" && len>0){
    listCameras();
    startCamera();
  }

});

onBeforeUnmount(() => {
  stopCamera();
  worker.terminate();
});
</script>

<template>
  <div class="onnx-runner-container">

    <div class="onnx-runner">

      <!-- Mode selector -->
       <div v-if="loadingState=='ok'"  style="width:100%">
       <div class="mode-row">
          <div class="mode">
            <label><input type="radio" value="live" v-model="mode" /> Live</label>
            <label style="margin-left:20px;">
              <input type="radio" value="single_image" v-model="mode" /> Upload
            </label>
          </div>

            <label class="checkbox">
              <input
                type="checkbox"
                v-model="hideBoxes"
              />
              Hide detection boxes
            </label>

          <div v-if="isProcessingImage" class="loading-container">
            <div class="spinner"></div>
          </div>
        </div>

          <div v-if="mode === 'single_image'">
            <button class="upload-button" @click="fileInput.click()">Select Image</button>
          </div>
           <div v-else>
            <label >Camera: </label>
                  <select class="upload-button" v-model="selectedCamera" @change="startCamera">
                    <option disabled value="">-- Select Camera --</option>
                    <option 
                      v-for="cam in cameraList" 
                      :key="cam.deviceId" 
                      :value="cam.deviceId"
                    >
                      {{ cam.label || "Camera " + camIndex(cam) }}
                    </option>
                  </select>
          </div>
      </div>


           <!-- Model status -->
      <div class="status">
        <div :class="[`state-${loadingState}`,'header']">
          <div v-if="loadingState=='loading'">Loading model…</div>
          <div v-else-if="loadingState=='error'" >{{ loadError }}</div>
          <div v-else-if="loadingState=='ok'" >Model:{{ loadedFileName }}</div>
          <div>Count:{{ count }}</div>
        </div>
      </div>

      <!-- Upload drag/drop -->
      <!-- <div
        v-if="mode === 'upload'"
        class="drop-zone"
        :class="{ dragging: isDragging }"
        @dragenter="onDragEnter"
        @dragleave="onDragLeave"
        @dragover="onDragOver"
        @drop="onDrop"
        @click="fileInput.click()"
      >
        <p>Click to select or drag & drop image</p>
      </div> -->

      <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="onFileSelected" />

      <!-- Shared viewer -->
      <div class="viewer">
        <video v-if="mode === 'live'" ref="videoEl" class="live"></video>
        <img v-if="mode === 'single_image'" ref="imgEl" />
        <canvas ref="canvasEl"  class="overlay" ></canvas>
      </div>
      <h1>{{ bottomMessage }}</h1>

    </div>
  </div>
</template>

<style scoped>
/* Outer container centers everything */
.onnx-runner-container {
  width: 100%;
  max-width: 800px;
  margin-top:4px;
  
  display: flex;
  padding: 10px;
  min-height: 100vh; /* fallback */
  min-height: 100dvh; /* iOS Safari: exclude dynamic toolbar */
  background-color: #ffffff;
}

.onnx-runner {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.header {
  color: #fbfbfb;
  display: flex;
  flex-direction: row;
  justify-items: left;
  padding: 2px;
  justify-content: space-between;
}

.status { 
  margin-bottom: 10px; 
  font-size: 14px; 
  border: 1px solid grey;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.checkbox {
  margin:auto;
}

.state-loading {
  background-color: rgb(211, 163, 5);
}

.state-ok {
  background-color: rgb(43, 76, 20);
}

.state-error {
  background-color: rgb(142, 31, 1);
}

.results-div {
  flex:1;
  overflow-y: scroll;
  overflow-x: hidden;
  background: #f9f9f9;
}


.mode {align-self: center; }
.mode-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  height: 42px;
 }

.upload-button {
  background: #074578;
  border: 2px solid rgb(27, 52, 125);
  border-radius: 6px;
  color: rgb(234, 233, 233);
  margin: 4px;
  padding: 1px 12px 1px 12px;
  
}
.upload-button:hover {
  background: #3078a4;
}
.drop-zone {
  width: 640px;
  height: 130px;
  border: 2px dashed #777;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  background-color: #fff;
}
.drop-zone.dragging {
  background: #d8ecff;
  border-color: #2196f3;
}

.viewer {
  position: relative;
  width: 100%;
}

.overlay,
.img, 
.live {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;      /* scale with parent */
  height: auto;     /* preserve stacking */
  object-fit: cover;
  pointer-events: none; /* optional */
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
    border: 1px solid grey;
}

.kv .row {
   padding-left: 6px;
  display: flex;
  flex-direction: row;
}

.kv .key {
  font-weight: bold;
  width: 140px;          /* left column */
  text-align: left;
}

.kv .value {
  width: 60%;          /* right column */
  text-align: left;   /* optional */
}



.spinner {
  width: 50px;
  height: 50px;
  border: 6px solid #ccc;
  border-top-color: #4285f4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
